/**
 * RulesTenantsModule - Admin module for managing church rules and tenets
 * Features: CRUD operations, ordering, sections, enable/disable, presentation mode
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  GripVertical,
  Save,
  X,
  Presentation,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Database,
  Image,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const RulesTenantsModule = ({ adminUser }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    section: 'General',
    enabled: true,
    bible_passage: '',
    bible_text: '',
    image_url: ''
  });

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, sectionsRes, orderRes] = await Promise.all([
        axios.get(`${API}/rules`),
        axios.get(`${API}/rules/sections`),
        axios.get(`${API}/rules/section-order`)
      ]);
      setRules(rulesRes.data);
      setSections(sectionsRes.data);
      
      // Use saved section order or derive from sections
      const savedOrder = orderRes.data?.sections || [];
      const allSectionNames = sectionsRes.data.map(s => s.name);
      // Merge: saved order first, then any new sections not in saved order
      const merged = [
        ...savedOrder.filter(s => allSectionNames.includes(s)),
        ...allSectionNames.filter(s => !savedOrder.includes(s))
      ];
      setSectionOrder(merged);
      
      // Expand all sections by default
      const expanded = {};
      sectionsRes.data.forEach(s => { expanded[s.name] = true; });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Error loading rules:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleSaveRule = async () => {
    setSaving(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/rules/${editingRule.id}`, formData);
      } else {
        await axios.post(`${API}/rules`, formData);
      }
      setEditingRule(null);
      setShowAddForm(false);
      setFormData({ title: '', content: '', section: 'General', enabled: true, bible_passage: '', bible_text: '', image_url: '' });
      await loadRules();
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Failed to save rule');
    }
    setSaving(false);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await axios.delete(`${API}/rules/${ruleId}`);
      await loadRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await axios.post(`${API}/rules/${ruleId}/toggle`);
      await loadRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setFormData({
      title: rule.title,
      content: rule.content,
      section: rule.section,
      enabled: rule.enabled,
      bible_passage: rule.bible_passage || '',
      bible_text: rule.bible_text || '',
      image_url: rule.image_url || ''
    });
    setShowAddForm(true);
  };

  const handleReorder = async (newOrder) => {
    setRules(newOrder);
    try {
      await axios.post(`${API}/rules/reorder`, {
        rule_ids: newOrder.map(r => r.id)
      });
    } catch (error) {
      console.error('Error reordering rules:', error);
      await loadRules();
    }
  };

  const handleOrderChange = async (ruleId, newOrder) => {
    try {
      // Update the rule's order
      await axios.put(`${API}/rules/${ruleId}`, { order: newOrder - 1 }); // Backend uses 0-indexed
      await loadRules(); // Reload to get sorted list
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleMoveSectionUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...sectionOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setSectionOrder(newOrder);
    try {
      await axios.put(`${API}/rules/section-order`, { sections: newOrder });
    } catch (error) {
      console.error('Error saving section order:', error);
    }
  };

  const handleMoveSectionDown = async (index) => {
    if (index === sectionOrder.length - 1) return;
    const newOrder = [...sectionOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setSectionOrder(newOrder);
    try {
      await axios.put(`${API}/rules/section-order`, { sections: newOrder });
    } catch (error) {
      console.error('Error saving section order:', error);
    }
  };

  const handleSeedRules = async () => {
    if (!window.confirm('This will add default church rules. Continue?')) return;
    try {
      const res = await axios.post(`${API}/rules/seed`);
      alert(res.data.message);
      await loadRules();
    } catch (error) {
      console.error('Error seeding rules:', error);
      alert('Failed to seed rules');
    }
  };

  const openPresentationMode = () => {
    window.open('/rules-presentation', '_blank');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Group rules by section
  const groupedRules = rules.reduce((acc, rule) => {
    const section = rule.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(rule);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            Church Rules & Tenets
          </h2>
          <p className="text-slate-500 mt-1">Manage and display church rules during announcements</p>
        </div>
        
        <div className="flex items-center gap-2">
          {rules.length === 0 && (
            <button
              onClick={handleSeedRules}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
            >
              <Database className="w-4 h-4" />
              Load Default Rules
            </button>
          )}
          <button
            onClick={openPresentationMode}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:opacity-90 transition-opacity"
            data-testid="presentation-mode-btn"
          >
            <Presentation className="w-4 h-4" />
            Presentation Mode
          </button>
          <button
            onClick={() => {
              setEditingRule(null);
              setFormData({ title: '', content: '', section: 'General', enabled: true, bible_passage: '', bible_text: '', image_url: '' });
              setShowAddForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            data-testid="add-rule-btn"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500">Total Rules</p>
          <p className="text-2xl font-bold text-slate-800">{rules.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500">Enabled</p>
          <p className="text-2xl font-bold text-emerald-600">{rules.filter(r => r.enabled).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500">Disabled</p>
          <p className="text-2xl font-bold text-slate-400">{rules.filter(r => !r.enabled).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500">Sections</p>
          <p className="text-2xl font-bold text-blue-600">{sections.length}</p>
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                {editingRule ? 'Edit Rule' : 'Add New Rule'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter rule title"
                    data-testid="rule-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Enter rule content..."
                    data-testid="rule-content-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Section</label>
                    <select
                      value={formData.section}
                      onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="General">General</option>
                      <option value="Spiritual Conduct">Spiritual Conduct</option>
                      <option value="Personal Conduct">Personal Conduct</option>
                      <option value="Dress Code">Dress Code</option>
                      <option value="Purification">Purification</option>
                      <option value="Worship Practice">Worship Practice</option>
                      <option value="Church Etiquette">Church Etiquette</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enabled}
                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">Enabled (visible in presentation)</span>
                    </label>
                  </div>
                </div>
                
                {/* Bible Passage Section */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Scripture Reference (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Bible Passage</label>
                      <input
                        type="text"
                        value={formData.bible_passage || ''}
                        onChange={(e) => setFormData({ ...formData, bible_passage: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="e.g., Exodus 20:3-5"
                        data-testid="bible-passage-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Verse Text</label>
                      <textarea
                        value={formData.bible_text || ''}
                        onChange={(e) => setFormData({ ...formData, bible_text: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                        placeholder="Enter the scripture text..."
                        data-testid="bible-text-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Image Section */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Slide Image (Optional)
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Image URL</label>
                    <input
                      type="url"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                      data-testid="image-url-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Enter a direct URL to an image (PNG, JPG, or WebP)</p>
                  </div>
                  {formData.image_url && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 mb-2">Preview:</p>
                      <div className="w-32 h-32 rounded-xl overflow-hidden border border-slate-200">
                        <img 
                          src={formData.image_url} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-3 pt-4">
                  <button
                    onClick={handleSaveRule}
                    disabled={saving || !formData.title || !formData.content}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid="save-rule-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {editingRule ? 'Update Rule' : 'Save Rule'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingRule(null);
                      setFormData({ title: '', content: '', section: 'General', enabled: true, bible_passage: '', bible_text: '', image_url: '' });
                    }}
                    className="flex items-center gap-2 px-6 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rules List by Section */}
      {rules.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Rules Added Yet</h3>
          <p className="text-slate-500 mb-4">Add church rules and tenets to display during announcements</p>
          <button
            onClick={handleSeedRules}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
          >
            <Database className="w-4 h-4" />
            Load Default CCC Rules
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sectionOrder
            .filter(section => groupedRules[section])
            .map((section, sectionIndex) => {
              const sectionRules = groupedRules[section];
              return (
            <div key={section} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div
                onClick={() => toggleSection(section)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSection(section); }}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-slate-800">{section}</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                    {sectionRules.length} {sectionRules.length === 1 ? 'rule' : 'rules'}
                  </span>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                    Slide Position: {sectionIndex + 1}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveSectionUp(sectionIndex); }}
                    disabled={sectionIndex === 0}
                    className={`p-1.5 rounded-lg transition-colors ${sectionIndex === 0 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-500 hover:bg-blue-100 hover:text-blue-600'}`}
                    title="Move section up"
                    data-testid={`section-move-up-${section.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveSectionDown(sectionIndex); }}
                    disabled={sectionIndex === sectionOrder.filter(s => groupedRules[s]).length - 1}
                    className={`p-1.5 rounded-lg transition-colors ${sectionIndex === sectionOrder.filter(s => groupedRules[s]).length - 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-500 hover:bg-blue-100 hover:text-blue-600'}`}
                    title="Move section down"
                    data-testid={`section-move-down-${section.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  {expandedSections[section] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>
              
              <AnimatePresence>
                {expandedSections[section] && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-slate-100">
                      <Reorder.Group
                        axis="y"
                        values={sectionRules}
                        onReorder={(newOrder) => {
                          const otherRules = rules.filter(r => r.section !== section);
                          handleReorder([...otherRules, ...newOrder]);
                        }}
                        className="divide-y divide-slate-100"
                      >
                        {sectionRules.map((rule, ruleIndex) => (
                          <Reorder.Item
                            key={rule.id}
                            value={rule}
                            className="flex items-start gap-4 p-4 bg-white hover:bg-slate-50 transition-colors cursor-grab active:cursor-grabbing"
                          >
                            <GripVertical className="w-5 h-5 text-slate-300 mt-1 flex-shrink-0" />
                            
                            {/* Order Number Selector */}
                            <div className="flex-shrink-0">
                              <select
                                value={rule.order || ruleIndex + 1}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleOrderChange(rule.id, parseInt(e.target.value));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-14 px-2 py-1 text-center border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                title="Slide order"
                              >
                                {Array.from({ length: rules.length }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-medium ${rule.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                                  {rule.title}
                                </h4>
                                {!rule.enabled && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                                    Disabled
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm ${rule.enabled ? 'text-slate-600' : 'text-slate-400'} line-clamp-2`}>
                                {rule.content}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRule(rule.id);
                                }}
                                className={`p-2 rounded-lg transition-colors ${
                                  rule.enabled 
                                    ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' 
                                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                }`}
                                title={rule.enabled ? 'Disable' : 'Enable'}
                              >
                                {rule.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRule(rule);
                                }}
                                className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRule(rule.id);
                                }}
                                className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </Reorder.Item>
                        ))}
                      </Reorder.Group>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

export default RulesTenantsModule;
